import { useEffect, useState } from "react";
import { api, API_BASE, fmtINR } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProjectReports() {
  const [customers, setCustomers] = useState([]);
  const [pms, setPms] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [f, setF] = useState({ status: "", customer_id: "", project_manager_id: "", start_date: "", end_date: "" });
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/accounts").then((r) => setCustomers(r.data || []));
    api.get("/employees/lookup/all").then((r) => setPms(r.data || []));
    api.get("/projects/statuses").then((r) => setStatuses(r.data.statuses || []));
  }, []);

  const load = () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/projects", { params: { ...params, page_size: 200 } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load projects"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async () => {
    const tok = localStorage.getItem("smipl_token");
    const params = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
    const url = `${API_BASE}/projects/reports/export?${params}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
    const blob = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "projects_report.xlsx"; a.click();
    URL.revokeObjectURL(a.href);
  };

  const rows = data?.items || [];
  const totals = rows.reduce((acc, p) => {
    acc.value += p.project_value || 0; acc.cost += p.total_cost || 0;
    acc.profit += p.estimated_profit || 0; acc.billed += p.amount_billed || 0;
    acc.received += p.amount_received || 0; return acc;
  }, { value: 0, cost: 0, profit: 0, billed: 0, received: 0 });

  return (
    <div className="space-y-4">
      <PageHeader title="Project Reports" description="Filter, review and export project performance."
                  action={<Button onClick={download}><DownloadSimple className="mr-2" /> Export Excel</Button>} />
      <Card className="p-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div><div className="text-xs opacity-60 mb-1">Status</div>
            <Select value={f.status || "__all__"} onValueChange={(v) => setF({ ...f, status: v === "__all__" ? "" : v })}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">All</SelectItem>
                {statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><div className="text-xs opacity-60 mb-1">Customer</div>
            <Select value={f.customer_id || "__all__"} onValueChange={(v) => setF({ ...f, customer_id: v === "__all__" ? "" : v })}>
              <SelectTrigger className="w-52"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">All</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><div className="text-xs opacity-60 mb-1">Project Manager</div>
            <Select value={f.project_manager_id || "__all__"} onValueChange={(v) => setF({ ...f, project_manager_id: v === "__all__" ? "" : v })}>
              <SelectTrigger className="w-52"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">All</SelectItem>
                {pms.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><div className="text-xs opacity-60 mb-1">From</div><Input type="date" className="w-36" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
          <div><div className="text-xs opacity-60 mb-1">To</div><Input type="date" className="w-36" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></div>
          <Button onClick={load}>Apply</Button>
        </div>
      </Card>

      <div className="grid md:grid-cols-5 gap-3">
        {[
          ["Total Value", totals.value], ["Total Cost", totals.cost],
          ["Est. Profit", totals.profit], ["Amount Billed", totals.billed],
          ["Amount Received", totals.received],
        ].map(([l, v]) => (
          <Card key={l} className="p-3">
            <div className="text-[10px] opacity-60 uppercase tracking-wider">{l}</div>
            <div className="text-lg font-semibold tabular-nums">{fmtINR(v)}</div>
          </Card>
        ))}
      </div>

      <Card className="p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/40"><tr>{["Code", "Name", "Customer", "Status", "Progress", "Value", "Cost", "Profit", "Margin %"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-6 opacity-60">No projects match the filters.</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">{p.code}</td>
                <td className="px-3 py-2">{p.name}</td>
                <td className="px-3 py-2 text-xs opacity-70">{p.customer_name}</td>
                <td className="px-3 py-2 text-xs capitalize">{(p.status || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2 text-xs">{p.progress_pct}%</td>
                <td className="px-3 py-2 text-xs font-mono">{fmtINR(p.project_value)}</td>
                <td className="px-3 py-2 text-xs font-mono">{fmtINR(p.total_cost)}</td>
                <td className={`px-3 py-2 text-xs font-mono ${p.estimated_profit >= 0 ? "text-green-600" : "text-red-600"}`}>{fmtINR(p.estimated_profit)}</td>
                <td className="px-3 py-2 text-xs">{p.profit_margin_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
